import React, { useEffect, useState } from 'react';
import { ArrowForward, BackupOutlined, DeleteOutline, RestoreOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';

const ProductBackupList = () => {
  const navigate = useNavigate();
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [backupToDelete, setBackupToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [backupToRestore, setBackupToRestore] = useState(null);
  const [restorePassword, setRestorePassword] = useState('');
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    let active = true;
    api.getProductBackups()
      .then((response) => {
        const savedBackups = (response?.data?.data || []).filter((backup) => (
          typeof backup?.level3Name === 'string'
          && Number.isFinite(backup?.productCount)
        ));
        if (active) setBackups(savedBackups);
      })
      .catch(() => { if (active) setError('Saved backups could not be loaded.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const handleConfirmDelete = async () => {
    if (!backupToDelete) return;
    setDeleting(true);
    try {
      await api.deleteProductBackup(backupToDelete._id);
      setBackups((prev) => prev.filter((b) => b._id !== backupToDelete._id));
      toast.success(`Backup for "${backupToDelete.level3Name}" deleted successfully`);
      setBackupToDelete(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not delete the backup.');
    } finally {
      setDeleting(false);
    }
  };

  const closeRestoreDialog = () => {
    if (!restoring) {
      setBackupToRestore(null);
      setRestorePassword('');
    }
  };

  const handleRestore = async () => {
    if (!backupToRestore || !restorePassword.trim()) return;
    setRestoring(true);
    try {
      const response = await api.restoreProductBackup(backupToRestore._id, restorePassword);
      const { restoredCount = 0, alreadyPresentCount = 0 } = response?.data?.data || {};
      toast.success(`${restoredCount} product(s) restored${alreadyPresentCount ? `; ${alreadyPresentCount} already present` : ''}.`);
      setBackupToRestore(null);
      setRestorePassword('');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not restore this backup.');
    } finally {
      setRestoring(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto', pb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2 }}>
        <Box>
          <Typography sx={{ color: '#1D1F2C', fontSize: { xs: 28, md: 32 }, fontWeight: 700 }}>Saved Product Backups</Typography>
          <Typography sx={{ color: '#667085', fontSize: 16, mt: 0.5 }}>Only backups created by you appear here. Open one to view its saved products.</Typography>
        </Box>
        <Button variant="contained" startIcon={<BackupOutlined />} onClick={() => navigate('/backup/create')} sx={{ textTransform: 'none', backgroundColor: '#512DA8', '&:hover': { backgroundColor: '#432584' } }}>Create backup</Button>
      </Box>
      <Paper variant="outlined" sx={{ borderColor: '#E0E2E7', borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer><Table sx={{ minWidth: 920 }}>
          <TableHead><TableRow sx={{ backgroundColor: '#F9FAFB' }}>
            <TableCell sx={{ fontWeight: 700 }}>Subcategory Level 3</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Subcategory</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Products</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Last backed up</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>Action</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {backups.map((backup) => (
              <TableRow key={backup._id} hover>
                <TableCell sx={{ fontWeight: 700 }}>{backup.level3Name}</TableCell>
                <TableCell>{backup.categoryName || '-'}</TableCell>
                <TableCell>{backup.subcategoryName || '-'}</TableCell>
                <TableCell><Chip label={`${backup.productCount} products`} size="small" sx={{ color: '#512DA8', backgroundColor: '#F3EEFF', fontWeight: 700 }} /></TableCell>
                <TableCell>{new Date(backup.updatedAt || backup.createdAt).toLocaleString()}</TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'inline-flex', gap: 1, alignItems: 'center' }}>
                    <Button
                      variant="contained"
                      endIcon={<ArrowForward />}
                      onClick={() => navigate(`/backup/${backup._id}`)}
                      sx={{ textTransform: 'none', backgroundColor: '#512DA8', '&:hover': { backgroundColor: '#432584' } }}
                    >
                      View products
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<RestoreOutlined />}
                      onClick={() => setBackupToRestore(backup)}
                      sx={{
                        textTransform: 'none',
                        borderColor: '#7F56D9',
                        color: '#512DA8',
                        backgroundColor: '#fff',
                        '&:hover': { borderColor: '#512DA8', backgroundColor: '#F9F5FF' },
                      }}
                    >
                      Restore all products
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteOutline />}
                      onClick={() => setBackupToDelete(backup)}
                      sx={{
                        textTransform: 'none',
                        borderColor: '#FDA29B',
                        color: '#D92D20',
                        backgroundColor: '#FFF',
                        '&:hover': {
                          borderColor: '#F04438',
                          backgroundColor: '#FEE4E2',
                        },
                      }}
                    >
                      Delete
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {!backups.length && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 7, color: '#667085' }}>No saved backups yet. Create a backup from Level 3 Categories.</TableCell></TableRow>}
          </TableBody>
        </Table></TableContainer>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={Boolean(backupToDelete)}
        onClose={() => !deleting && setBackupToDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#1D1F2C', pb: 1 }}>
          Delete Product Backup
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#475467', fontSize: 14 }}>
            Are you sure you want to delete the backup for <strong>{backupToDelete?.level3Name}</strong>?
            This will permanently remove the saved snapshot of {backupToDelete?.productCount} products.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setBackupToDelete(null)}
            disabled={deleting}
            sx={{ textTransform: 'none', color: '#667085' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={deleting}
            variant="contained"
            color="error"
            sx={{
              textTransform: 'none',
              backgroundColor: '#D92D20',
              '&:hover': { backgroundColor: '#B42318' },
            }}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(backupToRestore)} onClose={closeRestoreDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#1D1F2C', pb: 1 }}>Restore all products</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#475467', fontSize: 14, mb: 2 }}>
            Restore the {backupToRestore?.productCount || 0} products saved in <strong>{backupToRestore?.level3Name}</strong> to All Products. Products that already exist will not be changed.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            required
            type="password"
            label="Admin password"
            value={restorePassword}
            onChange={(event) => setRestorePassword(event.target.value)}
            autoComplete="new-password"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeRestoreDialog} disabled={restoring} sx={{ textTransform: 'none', color: '#667085' }}>Cancel</Button>
          <Button
            onClick={handleRestore}
            disabled={restoring || !restorePassword.trim()}
            variant="contained"
            startIcon={<RestoreOutlined />}
            sx={{ textTransform: 'none', backgroundColor: '#512DA8', '&:hover': { backgroundColor: '#432584' } }}
          >
            {restoring ? 'Restoring...' : 'Restore all products'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductBackupList;
